import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { useFeature } from '@/hooks/useFeature';
import { useImageUpload } from '@/hooks/useImageUpload';
import { useOrganization, useOrganizations } from '@/hooks/useOrganizations';
import {
  addCustomDomainMutation,
  customDomainStatusQuery,
  removeCustomDomainMutation,
  updateOrganizationMutation,
} from '@/queries/organization.query';
import { CameraSolid } from '@mynaui/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Loader2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';

interface OrganizationEditorProps {
  onClose?: () => void;
}

interface FormValues {
  name: string;
  logoFile: File | null;
  removeLogo: boolean;
  authBrandingEnabled: boolean;
}

export function OrganizationEditor({ onClose }: OrganizationEditorProps) {
  const organization = useOrganization();
  const queryClient = useQueryClient();
  const { refreshOrganizations } = useOrganizations();
  const { uploadImage, isUploading, uploadProgress } = useImageUpload({
    organizationId: organization?.id || '',
    type: 'ORGANIZATION_LOGO',
  });
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation('orgSettings');
  const authBrandingGate = useFeature('organization.authBranding');
  const customDomainGate = useFeature('organization.customDomain');
  const [newDomain, setNewDomain] = useState('');

  const {
    data: customDomainData,
    refetch: refetchCustomDomain,
    isFetching: isCheckingCustomDomain,
  } = useQuery({
    queryKey: ['customDomainStatus', organization?.id],
    queryFn: () => execute(customDomainStatusQuery),
    enabled: !!organization?.id && customDomainGate.allowed,
    refetchInterval: (query) =>
      query.state.data?.customDomainStatus && query.state.data.customDomainStatus.status !== 'ACTIVE'
        ? 30_000
        : false,
  });
  const customDomain = customDomainData?.customDomainStatus ?? null;

  const handleCheckStatus = async () => {
    const result = await refetchCustomDomain();
    const checked = result.data?.customDomainStatus;
    if (checked?.status === 'ACTIVE') toast.success(t('customDomain.checked.active'));
    else if (checked?.status === 'FAILED') toast.error(t('customDomain.checked.failed'));
    else if (checked?.dnsVerified) toast.success(t('customDomain.checked.issuing'));
    else toast.info(t('customDomain.checked.pending'));
  };

  const addDomain = useMutation({
    mutationFn: (domain: string) => execute(addCustomDomainMutation, { domain }),
    onSuccess: () => {
      setNewDomain('');
      refetchCustomDomain();
    },
    onError: (error: Error) =>
      toast.error(t(`customDomain.errors.${error.message}`, { defaultValue: t('customDomain.errors.generic') })),
  });

  const removeDomain = useMutation({
    mutationFn: () => execute(removeCustomDomainMutation),
    onSuccess: () => refetchCustomDomain(),
    onError: () => toast.error(t('customDomain.errors.generic')),
  });

  const initialValues: FormValues = {
    name: organization?.name || '',
    logoFile: null,
    removeLogo: false,
    authBrandingEnabled: organization?.authBrandingEnabled || false,
  };

  const hasChanges = (values: FormValues) => {
    const nameChanged = values.name !== (organization?.name || '');
    const logoChanged = values.logoFile !== null;
    const logoRemoved = values.removeLogo;
    const brandingChanged = values.authBrandingEnabled !== (organization?.authBrandingEnabled || false);
    return nameChanged || logoChanged || logoRemoved || brandingChanged;
  };

  const mutation = useMutation({
    mutationFn: async (input: { name?: string; logoId?: string | null; authBrandingEnabled?: boolean }) => {
      if (!organization?.id) throw new Error('No organization ID');
      return execute(updateOrganizationMutation, { id: organization.id, input });
    },
    onSuccess: () => {
      analytics.capture('Organization Updated');
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast.success(t('editor.updatedSuccess'));

      formik.resetForm();
      setLogoPreview(null);
      setIsEditing(false);
      refreshOrganizations();
      onClose?.();
    },
    onError: (error) => {
      toast.error(t('editor.failedUpdate') + ': ' + error.message);
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (!organization?.id) {
      toast.error(t('editor.noOrganization'));
      return;
    }

    if (!hasChanges(values)) {
      toast.info(t('editor.noChanges'));
      return;
    }

    try {
      let logoId: string | undefined;

      if (values.logoFile) {
        try {
          logoId = await uploadImage(values.logoFile);
        } catch (error) {
          toast.error(t('editor.failedUploadLogo'));
          console.error('Logo upload error:', error);
          return;
        }
      }

      const updateInput: { name?: string; logoId?: string | null; authBrandingEnabled?: boolean } = {};

      if (values.name !== (organization?.name || '')) {
        updateInput.name = values.name.trim();
      }

      if (logoId) {
        updateInput.logoId = logoId;
      } else if (values.removeLogo && organization.logo?.url) {
        updateInput.logoId = null;
      }

      if (values.authBrandingEnabled !== (organization.authBrandingEnabled || false)) {
        updateInput.authBrandingEnabled = values.authBrandingEnabled;
      }

      await mutation.mutateAsync(updateInput);
    } catch (error) {
      console.error('Error updating organization:', error);
      toast.error(t('editor.failedUpdate') + ': ' + (error instanceof Error ? error.message : ''));
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  });

  const _handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('editor.selectImageFile'));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('editor.fileTooLarge'));
        return;
      }

      formik.setFieldValue('logoFile', file);
      const url = URL.createObjectURL(file);
      setLogoPreview(url);
      setIsEditing(true);
    }
  };

  const _handleCancel = () => {
    setIsEditing(false);
    setLogoPreview(null);
    formik.resetForm();
  };

  const _handleAuthBrandingChange = (checked: boolean) => {
    if (checked && !authBrandingGate.allowed) {
      authBrandingGate.promptUpgrade();
      return;
    }

    if (checked && !organization.logo?.url && !formik.values.logoFile) {
      toast.error(t('editor.authBrandingLogoRequired'));
      return;
    }

    formik.setFieldValue('authBrandingEnabled', checked);
  };

  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  if (!organization) {
    return (
      <Card>
        <CardContent className='p-6'>
          <div className='text-center text-gray-500'>{t('editor.noOrganization')}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader id='onboarding-org-settings'>
        <CardTitle>{t('editor.title')}</CardTitle>
        <CardDescription>{t('editor.description')}</CardDescription>
      </CardHeader>
      <CardContent className='gap-6'>
        <div className='flex items-center gap-6'>
          <div className='relative'>
            <img
              src={logoPreview || organization.logo?.url || getPlaceholderAvatar(organization.name || organization.id)}
              alt=''
              className='size-20 object-cover rounded-full border border-slate-200/70 dark:border-stone-800/70'
            />
            <label
              htmlFor='logo-upload'
              className={`absolute -bottom-1 -right-1 bg-slate-900 hover:bg-slate-950 text-white dark:bg-white dark:text-stone-900 rounded-full p-2 transition-colors ${
                isUploading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
              }`}>
              <CameraSolid className='size-4 stroke-2' />
              <input
                id='logo-upload'
                type='file'
                accept='image/*'
                className='hidden'
                onChange={_handleLogoUpload}
                disabled={isUploading}
              />
            </label>
            {isUploading && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/50 rounded-full'>
                <span className='text-white text-xs font-medium'>{uploadProgress}%</span>
              </div>
            )}
          </div>
          <div className='flex flex-col gap-1.5'>
            <h3 className='text-lg font-medium leading-none text-slate-900 dark:text-white'>{organization.name}</h3>
            <p className='text-slate-500 dark:text-stone-400 text-sm font-medium leading-none'>
              {organization.slug}
            </p>
            {logoPreview && (
              <p className='text-xs text-slate-600 dark:text-stone-400'>{t('editor.newLogoSelected')}</p>
            )}
          </div>
        </div>
        <Separator />
        <form onSubmit={formik.handleSubmit}>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div className='space-y-2'>
              <Label htmlFor='name'>{t('editor.organizationName')}</Label>
              <Input
                id='name'
                name='name'
                value={formik.values.name}
                onChange={formik.handleChange}
                placeholder={t('editor.organizationNamePlaceholder')}
                required
                disabled={!isEditing}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='slug'>{t('editor.slug')}</Label>
              <Input id='slug' value={organization.slug || ''} disabled />
            </div>
          </div>
          <div className='mt-6 flex items-center justify-between gap-4 rounded-md border border-slate-200/70 p-4 dark:border-stone-800/70'>
            <div className='space-y-1'>
              <Label htmlFor='auth-branding'>{t('editor.authBranding')}</Label>
              <p className='text-sm text-muted-foreground'>{t('editor.authBrandingDescription')}</p>
              {!authBrandingGate.allowed && (
                <button
                  type='button'
                  className='text-sm font-medium text-primary underline underline-offset-4'
                  onClick={authBrandingGate.promptUpgrade}>
                  {t('editor.authBrandingUpgrade')}
                </button>
              )}
            </div>
            <Switch
              id='auth-branding'
              checked={formik.values.authBrandingEnabled}
              onCheckedChange={_handleAuthBrandingChange}
              disabled={!isEditing}
            />
          </div>
          <div className='flex justify-end space-x-2 mt-4'>
            {!isEditing ? (
              <Button type='button' onClick={() => setIsEditing(true)}>
                {t('editor.changeDetails')}
              </Button>
            ) : (
              <>
                <Button
                  type='button'
                  variant='outline'
                  onClick={_handleCancel}
                  disabled={isUploading || mutation.isPending}>
                  {t('editor.cancel')}
                </Button>
                <Button type='submit' disabled={!hasChanges(formik.values) || isUploading || mutation.isPending}>
                  {isUploading || mutation.isPending ? (
                    <>
                      <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                      {isUploading ? t('editor.uploading', { progress: Math.round(uploadProgress) }) : t('editor.saving')}
                    </>
                  ) : (
                    t('editor.saveChanges')
                  )}
                </Button>
              </>
            )}
          </div>
        </form>
        {/* Custom domain (PRO) */}
        <div className='mt-6 rounded-md border border-slate-200/70 p-4 dark:border-stone-800/70'>
          <div className='space-y-1'>
            <Label>{t('customDomain.title')}</Label>
            <p className='text-sm text-muted-foreground'>{t('customDomain.description')}</p>
            {!customDomainGate.allowed && (
              <button
                type='button'
                className='text-sm font-medium text-primary underline underline-offset-4'
                onClick={customDomainGate.promptUpgrade}>
                {t('customDomain.upgrade')}
              </button>
            )}
          </div>
          {customDomainGate.allowed && (
            <div className='mt-3 space-y-3'>
              {customDomain ? (
                <>
                  <div className='flex items-center gap-2'>
                    <span className='font-mono text-sm'>{customDomain.domain}</span>
                    <Badge
                      variant={
                        customDomain.status === 'ACTIVE'
                          ? 'success'
                          : customDomain.status === 'FAILED'
                            ? 'destructive'
                            : customDomain.dnsVerified
                              ? 'warning'
                              : 'secondary'
                      }>
                      {customDomain.status === 'PENDING_DNS' && customDomain.dnsVerified
                        ? t('customDomain.status.ISSUING')
                        : t(`customDomain.status.${customDomain.status}`)}
                    </Badge>
                  </div>
                  {customDomain.status !== 'ACTIVE' &&
                    (customDomain.dnsVerified ? (
                      <div className='rounded-md bg-green-50 p-3 text-sm text-green-900 dark:bg-green-950/40 dark:text-green-200'>
                        <p>{t('customDomain.dnsVerifiedInfo')}</p>
                      </div>
                    ) : (
                      <div className='rounded-md bg-slate-50 p-3 text-sm dark:bg-stone-900'>
                        <p>{t('customDomain.cnameInstructions')}</p>
                        <div className='mt-2 flex items-center gap-2'>
                          <code className='text-xs'>
                            {customDomain.domain} CNAME {customDomain.cnameTarget}
                          </code>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => {
                              navigator.clipboard.writeText(customDomain.cnameTarget);
                              toast.success(t('customDomain.copied'));
                            }}>
                            {t('customDomain.copy')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  <div className='flex gap-2'>
                    {customDomain.status !== 'ACTIVE' && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        disabled={isCheckingCustomDomain}
                        onClick={handleCheckStatus}>
                        {isCheckingCustomDomain && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                        {t('customDomain.checkStatus')}
                      </Button>
                    )}
                    <Button
                      type='button'
                      variant='destructive'
                      size='sm'
                      disabled={removeDomain.isPending}
                      onClick={() => removeDomain.mutate()}>
                      {t('customDomain.remove')}
                    </Button>
                  </div>
                </>
              ) : (
                <div className='flex gap-2'>
                  <Input
                    placeholder={t('customDomain.placeholder')}
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                  <Button
                    type='button'
                    disabled={!newDomain.trim() || addDomain.isPending}
                    onClick={() => addDomain.mutate(newDomain.trim())}>
                    {addDomain.isPending ? <Loader2 className='h-4 w-4 animate-spin' /> : t('customDomain.add')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
